import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { SUBJECTS, localDateString, type Subject } from "@/lib/app-types";
import { MAX_LECTURES } from "@/lib/lecture-plan";
import { BottomSheet } from "./bottom-sheet";
import { RippleButton } from "./ripple-button";
import { DateField } from "./date-field";

export function AddChapterSheet({
  open,
  busy,
  onClose,
  onSubmit,
}: {
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (input: {
    subject: Subject;
    chapter: string;
    totalLectures: number;
    completedOn: string | null;
  }) => void;
}) {
  const [subject, setSubject] = useState<Subject>("Physics");
  const [chapter, setChapter] = useState("");
  const [lectures, setLectures] = useState(8);
  const [markAll, setMarkAll] = useState(false);
  const [date, setDate] = useState(localDateString());
  const submitted = useRef(false);

  useEffect(() => {
    if (open) {
      setChapter("");
      setMarkAll(false);
      setDate(localDateString());
      submitted.current = false;
    }
  }, [open]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Add chapter"
      subtitle="Pick a subject, name the chapter and set its lectures."
    >
      <div className="space-y-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Subject
          </p>
          <div className="mt-2.5 grid grid-cols-5 gap-2">
            {SUBJECTS.map((item) => {
              const active = subject === item;
              return (
                <motion.button
                  key={item}
                  type="button"
                  whileTap={{ scale: 0.94 }}
                  onClick={() => setSubject(item)}
                  className={cn(
                    "relative rounded-2xl px-2 py-2.5 text-[12px] font-semibold transition-colors",
                    active
                      ? "text-primary-foreground"
                      : "border border-hairline bg-surface text-muted-foreground",
                  )}
                >
                  {active ? (
                    <motion.span
                      layoutId="subject-pill"
                      transition={{ type: "spring", stiffness: 320, damping: 30 }}
                      className="absolute inset-0 rounded-2xl [background-image:var(--gradient-brand)]"
                    />
                  ) : null}
                  <span className="relative">{item}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Chapter name
          </p>
          <input
            value={chapter}
            onChange={(e) => setChapter(e.target.value)}
            placeholder="e.g. Electrostatics"
            maxLength={120}
            className="mt-2.5 w-full rounded-2xl border border-hairline bg-surface px-4 py-3 text-[15px] outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/50"
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Lectures in this chapter
            </p>
            <p className="text-sm font-bold text-primary tabular-nums">{lectures}</p>
          </div>
          <div className="mt-2.5 grid grid-cols-10 gap-1.5">
            {Array.from({ length: MAX_LECTURES }, (_, i) => i + 1).map((n) => (
              <motion.button
                key={n}
                type="button"
                whileTap={{ scale: 0.88 }}
                onClick={() => setLectures(n)}
                className={cn(
                  "rounded-xl py-2 text-[11px] font-bold tabular-nums transition-colors",
                  n === lectures
                    ? "text-primary-foreground [background-image:var(--gradient-brand)]"
                    : n < lectures
                      ? "bg-primary/15 text-primary"
                      : "border border-hairline bg-surface text-muted-foreground",
                )}
              >
                {n}
              </motion.button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setMarkAll((v) => !v)}
          className="flex w-full items-center gap-3 rounded-2xl border border-hairline bg-surface px-4 py-3 text-left"
        >
          <span
            className={cn(
              "relative h-6 w-11 shrink-0 rounded-full transition-colors",
              markAll ? "[background-image:var(--gradient-brand)]" : "bg-surface",
            )}
          >
            <motion.span
              layout
              transition={{ type: "spring", stiffness: 500, damping: 32 }}
              className={cn(
                "absolute top-1 size-4 rounded-full bg-white",
                markAll ? "left-6" : "left-1",
              )}
            />
          </span>
          <span className="text-[13px] font-medium">
            Mark all lectures completed on a date
            <span className="block text-[11px] text-muted-foreground">
              Otherwise tick lectures later, in any order.
            </span>
          </span>
        </button>

        {markAll ? <DateField value={date} onChange={setDate} /> : null}

        <RippleButton
          className="w-full"
          disabled={busy || chapter.trim().length === 0}
          onClick={() => {
            if (submitted.current) return;
            submitted.current = true;
            onSubmit({
              subject,
              chapter: chapter.trim(),
              totalLectures: lectures,
              completedOn: markAll ? date : null,
            });
          }}
        >
          {busy ? "Saving…" : "Add chapter"}
        </RippleButton>
      </div>
    </BottomSheet>
  );
}